import {
    useCreatePipelineJobs,
    PipelineStepName,
    RunMode,
} from '../../../hooks/admin/useAdminMutations'
import { AdminUserDto } from '../../../types/admin-types'
import { AxiosError } from 'axios'
import { Modal } from 'flowbite-react'
import React from 'react'

interface RunResearchModalProps {
    show: boolean
    onClose: () => void
    user: AdminUserDto | null
    isBulkRun?: boolean
    allUserIds?: string[]
}

const RunResearchModal: React.FC<RunResearchModalProps> = ({
    show,
    onClose,
    user,
    isBulkRun = false,
    allUserIds = [],
}) => {
    const { mutate: createJobs, isPending } = useCreatePipelineJobs()

    const handleRunResearch = () => {
        let user_ids_to_run: string[] | undefined
        let run_mode_to_use: RunMode
        let subject_name: string

        if (isBulkRun) {
            if (!allUserIds || allUserIds.length === 0) {
                alert('Error: No user IDs provided for bulk run.')
                return
            }
            user_ids_to_run = allUserIds
            run_mode_to_use = RunMode.ALL
            subject_name = 'all users'
        } else {
            if (!user) {
                alert('Error: No user selected.')
                return
            }
            user_ids_to_run = [user.id]
            run_mode_to_use = RunMode.INDIVIDUAL
            subject_name = user.name
        }

        createJobs(
            {
                user_ids: user_ids_to_run,
                step: PipelineStepName.RESEARCH,
                context: { run_mode: run_mode_to_use },
            },
            {
                onSuccess: (data) => {
                    const jobCount = data.jobs?.length || 0
                    alert(
                        `Successfully started research job(s) for ${subject_name}. (${jobCount} job(s) created)`,
                    )
                    onClose()
                },
                onError: (err: Error | AxiosError) => {
                    console.error('Error starting research job:', err)
                    const message =
                        err instanceof AxiosError
                            ? err.response?.data?.message || err.message
                            : err.message
                    alert(
                        `Failed to start research job for ${subject_name}: ${
                            message || 'Unknown error'
                        }`,
                    )
                },
            },
        )
    }

    const modalTitle = isBulkRun
        ? 'Run Research Step for All Users?'
        : `Run Research Step for ${user?.name || 'Selected User'}?`
    const buttonText = isBulkRun ? 'Yes, Run for All Users' : 'Yes, Run Research'

    if (!show || (!isBulkRun && !user)) {
        return null
    }

    return (
        <Modal show={show} size="md" onClose={onClose} popup>
            <Modal.Header />
            <Modal.Body>
                <div className="text-center">
                    <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
                        {modalTitle}
                    </h3>
                    <div className="flex justify-center gap-4">
                        <button
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={handleRunResearch}
                            disabled={isPending}
                        >
                            {isPending ? 'Starting Job(s)...' : buttonText}
                        </button>
                        <button
                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 disabled:opacity-50"
                            onClick={onClose}
                            disabled={isPending}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    )
}

export default RunResearchModal
